import { useState, useEffect } from "react";
import { API, G, GL, GB, BLACK, MID, LIGHT, BORDER, BG, sigConfidence } from "../constants";
import { GSC_SOURCES, SKU_SIGNALS, SOURCE_META } from "../data";
import { SourceLogo, IntentBadge, Ring } from "../components/shared";
import { PageHead, Footer } from "../components/Shell";

export function S02({ onBack, onNext, products, googleConnected, shopDomain }) {
  const [selectedSku, setSelectedSku] = useState(products[0].id);
  const [realGscData, setRealGscData] = useState({}); // { [productId]: gscQueries[] }
  const [redditData, setRedditData] = useState({}); // { [productId]: { posts, top_phrases, sentiment, ... } }
  const [aiSignals, setAiSignals] = useState({}); // { [productId]: { signals, count, source } }
  const [loadingSignals, setLoadingSignals] = useState(false);
  const [loadingReddit, setLoadingReddit] = useState(false);
  const [loadingAiSignals, setLoadingAiSignals] = useState(false);

  // Fetch real GSC data when Google is connected
  useEffect(() => {
    if (!googleConnected || !shopDomain) return;
    const fetchSignals = async () => {
      setLoadingSignals(true);
      try {
        // Get GSC sites
        const sitesRes = await fetch(`${API}/api/google/gsc/sites?shop=${encodeURIComponent(shopDomain)}`);
        if (!sitesRes.ok) { setLoadingSignals(false); return; }
        const sitesData = await sitesRes.json();
        const sites = sitesData.sites || [];
        if (!sites.length) { setLoadingSignals(false); return; }
        const siteUrl = sites[0].siteUrl;

        // Fetch queries for each product
        const data = {};
        for (const p of products.slice(0, 10)) {
          const handle = p.handle || p.title.toLowerCase().replace(/[^a-z0-9]+/g, "-");
          try {
            const res = await fetch(`${API}/api/google/gsc/product-queries?site_url=${encodeURIComponent(siteUrl)}&handle=${encodeURIComponent(handle)}&shop=${encodeURIComponent(shopDomain)}`);
            if (res.ok) {
              const d = await res.json();
              if (d.queries && d.queries.length > 0) {
                data[p.id] = d.queries;
              }
            }
          } catch (e) {}
        }
        setRealGscData(data);
      } catch (e) {
        /* logged */
      }
      setLoadingSignals(false);
    };
    fetchSignals();
  }, [googleConnected, shopDomain, products]);

  // Fetch Reddit signals for real products (no auth required)
  useEffect(() => {
    const hasDemo = products.some(p => !!SKU_SIGNALS[p.id]);
    if (hasDemo) return; // Demo products already have signals

    const fetchReddit = async () => {
      setLoadingReddit(true);
      const data = {};
      for (const p of products.slice(0, 10)) {
        try {
          const params = new URLSearchParams({
            query: p.title,
            brand: p.brand || p.vendor || "",
            product_type: p.product_type || p.shopifyData?.product_type || "",
            tags: Array.isArray(p.tags) ? p.tags.join(",") : (p.tags || ""),
            shop: shopDomain || "",
          });
          const res = await fetch(`${API}/api/social/reddit?${params}`);
          if (res.ok) {
            const d = await res.json();
            if (d.post_count > 0) {
              data[p.id] = d;
            }
          }
        } catch (e) {}
      }
      setRedditData(data);
      setLoadingReddit(false);
    };
    fetchReddit();
  }, [products, shopDomain]);

  // Fetch AI-generated search signals for all products (always runs — fills Search Demand when GSC is empty)
  useEffect(() => {
    const hasDemo = products.some(p => !!SKU_SIGNALS[p.id]);
    if (hasDemo) return;

    const fetchAiSignals = async () => {
      setLoadingAiSignals(true);
      const data = {};
      // Fire all requests in parallel for speed
      const promises = products.slice(0, 10).map(async (p) => {
        try {
          const res = await fetch(`${API}/api/signals/generate-search`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              title: p.title,
              description: p.description || p.shopifyData?.body_html?.replace(/<[^>]*>/g, "") || "",
              product_type: p.product_type || p.shopifyData?.product_type || "",
              tags: Array.isArray(p.tags) ? p.tags.join(",") : (p.tags || p.shopifyData?.tags || ""),
              vendor: p.vendor || p.shopifyData?.vendor || "",
              metafields: p.shopifyData?.metafields || {},
              shop: shopDomain || "",
              product_id: String(p.id),
            }),
          });
          if (res.ok) {
            const d = await res.json();
            if (d.signals && d.signals.length > 0) {
              data[p.id] = d;
            }
          }
        } catch (e) {}
      });
      await Promise.all(promises);
      setAiSignals(data);
      setLoadingAiSignals(false);
    };
    fetchAiSignals();
  }, [products, shopDomain]);

  const [activeGroup, setActiveGroup] = useState(0);

  const hasDemoSignals = !!SKU_SIGNALS[selectedSku];
  const gscQueriesForSku = realGscData[selectedSku] || [];
  const hasRealGsc = gscQueriesForSku.length > 0;
  const redditForSku = redditData[selectedSku] || {};
  const hasReddit = (redditForSku.top_phrases || []).length > 0;
  const aiSignalsForSku = aiSignals[selectedSku] || {};
  const hasAiSignals = (aiSignalsForSku.signals || []).length > 0;

  // Build signal groups - use demo data if available, else build from real data
  const groups = hasDemoSignals
    ? SKU_SIGNALS[selectedSku].groups
    : (() => {
        const g = [];
        // Search demand group: GSC data first, AI-inferred as fallback
        const searchSignals = hasRealGsc
          ? gscQueriesForSku.map(q => ({
              phrase: q.query,
              sources: ["gsc"],
              intent: q.position < 20 ? "high" : "med",
              volume: `${q.clicks} clicks - ${q.impressions} imp - pos ${q.position}`,
            }))
          : hasAiSignals
            ? (aiSignalsForSku.signals || []).map(s => ({
                phrase: s.phrase,
                sources: [s.source_hint === "reddit" ? "reddit" : s.source_hint === "tiktok" ? "tiktok" : "gsc"],
                intent: s.intent || "med",
                volume: s.type ? s.type.replace("_", " ") : "inferred",
              }))
            : [];
        g.push({
          key: "search",
          label: "Search Demand",
          icon: "Q",
          color: "#4285f4",
          desc: hasRealGsc
            ? "Real search queries from Google Search Console for this product"
            : hasAiSignals
              ? `AI-inferred search signals from product data - ${aiSignalsForSku.count || 0} long-tail queries generated`
              : loadingAiSignals
                ? "Generating search intelligence from product data..."
                : "Connect Google Search Console to see real search queries, or wait for AI signal generation",
          signals: searchSignals,
        });
        // Community voice group (Reddit)
        if (hasReddit) {
          g.push({
            key: "community",
            label: "Community Voice",
            icon: "C",
            color: "#ff4500",
            desc: `Consumer language from Reddit - ${redditForSku.post_count} posts across r/${(redditForSku.subreddits || []).slice(0,3).join(", r/")}`,
            signals: (redditForSku.top_phrases || []).slice(0, 15).map(p => ({
              phrase: p.phrase,
              sources: ["reddit"],
              intent: p.count >= 4 ? "high" : p.count >= 2 ? "med" : "low",
              volume: `${p.count} mentions`,
            })),
          });
        }
        return g;
      })();

  const group = groups[activeGroup] || groups[0];

  const totalSig   = (id) => {
    if (SKU_SIGNALS[id]) return SKU_SIGNALS[id].groups.reduce((s, g) => s + g.signals.length, 0);
    return (realGscData[id] || []).length + ((redditData[id] || {}).top_phrases || []).length + ((aiSignals[id] || {}).signals || []).length;
  };
  const multiCount = (id) => {
    if (SKU_SIGNALS[id]) return SKU_SIGNALS[id].groups.reduce((s, g) => s + g.signals.filter(sig => sig.sources.length >= 3).length, 0);
    return 0;
  };

  return (
    <>
      <PageHead
        num="02" title="Search Intelligence"
        sub="Consumer signals grouped by source type. Multi-source signals are weighted highest - the same phrase seen on Reddit, YouTube and TikTok is exactly what LLMs have trained on."
        onBack={onBack} onNext={onNext}
      />

      {/* Loading signals indicator */}
      {(loadingSignals || loadingReddit || loadingAiSignals) && (
        <div style={{ padding: "12px 16px", background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 6, marginBottom: 16, display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 12, height: 12, border: "2px solid #bae6fd", borderTopColor: "#0369a1", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
          <span style={{ fontSize: 12, color: "#0c4a6e" }}>
            {loadingAiSignals ? "Generating search intelligence from product data..."
              : loadingSignals && loadingReddit ? "Fetching search data + Reddit signals..."
              : loadingSignals ? "Fetching real search data from Google Search Console..."
              : "Fetching Reddit community signals..."}
          </span>
        </div>
      )}

      {/* No signals notice for real products */}
      {!hasDemoSignals && !hasRealGsc && !hasAiSignals && !hasReddit && !loadingSignals && !loadingReddit && !loadingAiSignals && (
        <div style={{ padding: "14px 18px", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, marginBottom: 16, display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 16, flexShrink: 0 }}>📡</span>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#92400e", marginBottom: 2 }}>No signal data connected yet</div>
            <div style={{ fontSize: 11, color: "#b45309" }}>Go back to Setup and connect Google Search Console + Analytics to see real search queries for your products.</div>
          </div>
        </div>
      )}

      {/* Beta sources legend */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        <span style={{ fontSize: 10, color: LIGHT, fontWeight: 600 }}>SOURCES:</span>
        {Object.entries(GSC_SOURCES).map(([k, v]) => (
          <span key={k} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10, color: v.beta ? LIGHT : MID, fontWeight: 500 }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: v.color, opacity: v.beta ? 0.35 : 1, border: v.beta ? "1px dashed rgba(0,0,0,0.15)" : "none", flexShrink: 0 }} />
            {v.label}
            {v.beta && <span style={{ fontSize: 8, fontWeight: 800, color: "#7c3aed", background: "#ede9fe", border: "1px solid #c4b5fd", borderRadius: 2, padding: "0 3px", lineHeight: "11px" }}>BETA</span>}
          </span>
        ))}
      </div>

      {/* ── Signal confidence summary ── */}
      {(() => {
        const p = products.find(x => x.id === selectedSku);
        const topSignals = (p && p.signalAttribution || [])
          .sort((a, b) => (b.sources || []).length - (a.sources || []).length)
          .slice(0, 4);
        return (
          <div style={{ border: `1px solid ${GB}`, borderRadius: 8, background: GL, padding: "14px 16px", marginBottom: 16, display: "flex", alignItems: "flex-start", gap: 20 }}>
            <div style={{ flexShrink: 0 }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: G, textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 6 }}>Top recommended signals</div>
              <div style={{ fontSize: 11, color: MID }}>Highest-confidence phrases for {p.title}</div>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", flex: 1 }}>
              {topSignals.map((sig, i) => {
                const conf = (sig.sources || []).length;
                const barW = Math.round((conf / 6) * 100);
                const col  = conf >= 4 ? G : conf >= 3 ? "#4285f4" : "#d97706";
                return (
                  <div key={`agree-${sig.phrase}`} style={{ background: BG, border: `1px solid ${BORDER}`, borderRadius: 6, padding: "8px 12px", minWidth: 160 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: BLACK, marginBottom: 5 }}>"{sig.phrase}"</div>
                    <div style={{ height: 4, background: BORDER, borderRadius: 2, marginBottom: 5, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${barW}%`, background: col, borderRadius: 2, transition: "width 0.6s ease" }} />
                    </div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <span style={{ fontSize: 10, color: col, fontWeight: 700 }}>{sigConfidence(sig.sources)}</span>
                      <span style={{ fontSize: 10, color: LIGHT }}>{conf} source{conf !== 1 ? "s" : ""}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Reddit sentiment summary for real products */}
      {!hasDemoSignals && hasReddit && (
        <div data-testid="reddit-signal-summary" style={{ border: `1px solid #fed7aa`, borderRadius: 8, background: "#fff7ed", padding: "14px 16px", marginBottom: 16, display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#ff4500", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <span style={{ color: "#fff", fontWeight: 900, fontSize: 16 }}>R</span>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#9a3412", marginBottom: 3 }}>
              Reddit: {redditForSku.post_count} posts · {(redditForSku.top_phrases || []).length} phrases extracted
              {redditForSku.sentiment && (
                <span style={{
                  marginLeft: 8, fontSize: 10, fontWeight: 800, padding: "2px 7px", borderRadius: 3,
                  background: redditForSku.sentiment.overall === "positive" ? "#dcfce7" : redditForSku.sentiment.overall === "negative" ? "#fef2f2" : "#fef3c7",
                  color: redditForSku.sentiment.overall === "positive" ? "#166534" : redditForSku.sentiment.overall === "negative" ? "#dc2626" : "#92400e",
                  textTransform: "uppercase", letterSpacing: "0.4px",
                }}>{redditForSku.sentiment.overall}</span>
              )}
            </div>
            <div style={{ fontSize: 11, color: "#9a3412" }}>
              Across r/{(redditForSku.subreddits || []).slice(0,4).join(", r/")}
              {redditForSku.comment_count > 0 && ` · ${redditForSku.comment_count} comments analysed`}
              <span style={{ marginLeft: 8, fontSize: 10, color: "#c2410c", fontStyle: "italic" }}>
                Mode: {redditForSku.mode === "oauth" ? "Authenticated API" : "Public JSON"}
              </span>
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", maxWidth: 300 }}>
            {(redditForSku.top_phrases || []).slice(0, 4).map((p) => (
              <span key={`rd-${p.phrase}`} style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 4, background: "#fff", border: "1px solid #fed7aa", color: "#9a3412" }}>
                "{p.phrase}" <span style={{ color: "#c2410c", fontWeight: 400 }}>×{p.count}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "220px 1fr", gap: 16, minHeight: 500 }}>

        {/* ── LEFT: SKU SELECTOR ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: LIGHT, letterSpacing: "1px", textTransform: "uppercase", marginBottom: 2 }}>Select SKU</div>

          {products.map(p => {
            const on = selectedSku === p.id;
            return (
              <div key={p.id} onClick={() => { setSelectedSku(p.id); setActiveGroup(0); }}
                style={{ border: `1px solid ${on ? GB : BORDER}`, borderRadius: 8, padding: "12px 14px", cursor: "pointer", background: on ? GL : "#fff", borderLeft: `3px solid ${on ? G : "transparent"}`, transition: "all 0.15s" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: on ? G : BLACK, textTransform: "uppercase", letterSpacing: "0.3px", marginBottom: 3 }}>{p.title}</div>
                <div style={{ fontSize: 11, color: LIGHT }}>{totalSig(p.id)} signals · {multiCount(p.id)} multi-source</div>
                {on && (
                  <div style={{ display: "flex", gap: 3, marginTop: 8, flexWrap: "wrap" }}>
                    {Object.keys(SOURCE_META).map(s => <span key={s} title={SOURCE_META[s].label}><SourceLogo src={s} size={14} /></span>)}
                  </div>
                )}
              </div>
            );
          })}

          <div style={{ marginTop: 6, border: `1px solid ${GB}`, borderRadius: 8, padding: "13px", background: GL }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: G, textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 7 }}>Multi-source = highest value</div>
            <div style={{ fontSize: 12, color: MID, lineHeight: 1.7 }}>
              When the same phrase appears across Reddit, YouTube <em>and</em> TikTok, it's been trained into LLMs multiple times. These signals are flagged{" "}
              <span style={{ display: "inline-flex", alignItems: "center", gap: 3, verticalAlign: "middle" }}>
                <SourceLogo src="reddit" size={13} /><SourceLogo src="youtube" size={13} /><SourceLogo src="tiktok" size={13} />
              </span>{" "}
              and weighted highest in copy generation.
            </div>
          </div>
        </div>

        {/* ── RIGHT: SIGNAL PANEL ── */}
        <div style={{ border: `1px solid ${BORDER}`, borderRadius: 8, overflow: "hidden", display: "flex", flexDirection: "column" }}>

          {/* Group tabs */}
          <div style={{ display: "flex", borderBottom: `1px solid ${BORDER}`, background: "#fafafa" }}>
            {groups.map((g, i) => {
              const on = activeGroup === i;
              const multi = g.signals.filter(s => s.sources.length >= 3).length;
              return (
                <button key={`grp-${g.key}`} onClick={() => setActiveGroup(i)}
                  style={{ flex: 1, border: "none", background: on ? "#fff" : "transparent", cursor: "pointer", padding: "11px 8px", fontFamily: "inherit", transition: "all 0.1s", borderBottom: on ? `2px solid ${g.color}` : "2px solid transparent", marginBottom: -1 }}>
                  <div style={{ fontSize: 15, marginBottom: 2 }}>{g.icon}</div>
                  <div style={{ fontSize: 11, fontWeight: on ? 700 : 500, color: on ? BLACK : MID }}>{g.label}</div>
                  <div style={{ fontSize: 10, color: on ? g.color : LIGHT, fontWeight: 600, marginTop: 2 }}>
                    {g.signals.length} signals{multi > 0 ? ` · ${multi} multi` : ""}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Group description */}
          <div style={{ padding: "10px 16px", background: "#fafafa", borderBottom: `1px solid ${BORDER}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <p style={{ fontSize: 12, color: MID, margin: 0, lineHeight: 1.6 }}>{group.desc}</p>
            {group.signals.filter(s => s.sources.length >= 3).length > 0 && (
              <span style={{ fontSize: 11, fontWeight: 700, color: G, background: GL, border: `1px solid ${GB}`, padding: "2px 8px", borderRadius: 4, whiteSpace: "nowrap", flexShrink: 0 }}>
                {group.signals.filter(s => s.sources.length >= 3).length} multi-source ↑
              </span>
            )}
          </div>

          {/* Signal table */}
          <div style={{ flex: 1, overflowY: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${BORDER}`, background: "#fafafa" }}>
                  <th style={{ padding: "9px 16px", textAlign: "left", fontSize: 11, fontWeight: 700, color: LIGHT, textTransform: "uppercase", letterSpacing: "0.4px" }}>Consumer signal / search phrase</th>
                  <th style={{ padding: "9px 16px", textAlign: "left", fontSize: 11, fontWeight: 700, color: LIGHT, textTransform: "uppercase", letterSpacing: "0.4px", whiteSpace: "nowrap" }}>Seen on</th>
                  <th style={{ padding: "9px 16px", textAlign: "left", fontSize: 11, fontWeight: 700, color: LIGHT, textTransform: "uppercase", letterSpacing: "0.4px", whiteSpace: "nowrap" }}>Volume</th>
                  <th style={{ padding: "9px 16px", textAlign: "left", fontSize: 11, fontWeight: 700, color: LIGHT, textTransform: "uppercase", letterSpacing: "0.4px", whiteSpace: "nowrap" }}>Intent</th>
                </tr>
              </thead>
              <tbody>
                {group.signals.map((sig, i) => {
                  const isMulti = sig.sources.length >= 3;
                  return (
                    <tr key={`sig-${sig.phrase}-${i}`} style={{ borderBottom: `1px solid ${BORDER}`, background: isMulti ? "#f8fffe" : i % 2 === 0 ? "#fff" : "#fafafa" }}>

                      {/* Phrase */}
                      <td style={{ padding: "11px 16px", fontSize: 13, fontWeight: 600, color: BLACK, lineHeight: 1.45, maxWidth: 340 }}>
                        {isMulti && (
                          <span style={{ display: "inline-block", width: 6, height: 6, borderRadius: "50%", background: G, marginRight: 7, verticalAlign: "middle", flexShrink: 0 }} />
                        )}
                        {sig.phrase}
                      </td>

                      {/* Source logos */}
                      <td style={{ padding: "11px 16px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          {sig.sources.map(s => (
                            <span key={s} title={`${GSC_SOURCES[s]?.label || s}${GSC_SOURCES[s]?.beta ? " (Beta)" : ""}`} style={{ display: "inline-flex", flexShrink: 0, position: "relative" }}>
                              <SourceLogo src={s} size={20} />
                              {GSC_SOURCES[s]?.beta && <span style={{ position: "absolute", top: -4, right: -6, fontSize: 6, fontWeight: 800, color: "#7c3aed", background: "#ede9fe", border: "1px solid #c4b5fd", borderRadius: 2, padding: "0 2px", lineHeight: "10px" }}>β</span>}
                            </span>
                          ))}
                          {isMulti && (
                            <span style={{ fontSize: 10, fontWeight: 800, color: G, background: GL, border: `1px solid ${GB}`, padding: "1px 5px", borderRadius: 3, marginLeft: 3, whiteSpace: "nowrap" }}>high value</span>
                          )}
                        </div>
                      </td>

                      {/* Volume */}
                      <td style={{ padding: "11px 16px", fontSize: 12, color: MID, whiteSpace: "nowrap", fontWeight: 500 }}>{sig.volume}</td>

                      {/* Intent */}
                      <td style={{ padding: "11px 16px" }}><IntentBadge intent={sig.intent} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Footer */}
          <div style={{ padding: "10px 16px", background: GL, borderTop: `1px solid ${GB}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
            <span style={{ fontSize: 12, color: G, fontWeight: 600 }}>
              ↑ All high-intent and multi-source signals are embedded into AI copy generation for {products.find(p => p.id === selectedSku)?.title}
            </span>
            <div style={{ display: "flex", gap: 3, alignItems: "center" }}>
              {Object.keys(SOURCE_META).map(s => <span key={s} title={SOURCE_META[s].label}><SourceLogo src={s} size={14} /></span>)}
            </div>
          </div>
        </div>
      </div>

      <Footer onBack={onBack} onNext={onNext} />
    </>
  );
}

// ─── STEP 03 ─────────────────────────────────────────────────────────────────


